/**
 * SyncLogger - Sistema de logging estruturado para sincronização
 *
 * Fornece logs estruturados, persistentes e exportáveis para diagnóstico
 * de problemas de sincronização.
 */

import uuid from 'react-uuid'

const MAX_LOGS = 500
const LOGS_STORAGE_KEY = 'sync_logs'

class SyncLogger {
  constructor() {
    this.sessionId = uuid()
    this.syncId = null
    this.enabled = true
  }

  /**
   * Inicia uma nova sessão de sync e retorna o ID
   */
  startSync(type = 'auto') {
    this.syncId = uuid()
    this.log('SYNC_START', {
      type,
      syncId: this.syncId,
    })
    return this.syncId
  }

  /**
   * Finaliza a sessão de sync atual
   */
  endSync(success, details = {}) {
    this.log(success ? 'SYNC_SUCCESS' : 'SYNC_FAILURE', {
      syncId: this.syncId,
      ...details,
    })
    this.syncId = null
  }

  /**
   * Log genérico com contexto automático
   */
  log(event, data = {}) {
    if (!this.enabled) return

    const entry = {
      id: uuid(),
      timestamp: new Date().toISOString(),
      timestampMs: Date.now(),
      sessionId: this.sessionId,
      syncId: this.syncId,
      event,
      data,
      context: this._getContext(),
    }

    // Console log formatado
    this._consoleLog(entry)

    // Persistir
    this._persist(entry)

    return entry
  }

  /**
   * Log de erro com stack trace
   */
  error(event, error, additionalData = {}) {
    return this.log(event, {
      error: {
        message: error?.message || String(error),
        stack: error?.stack,
        name: error?.name,
      },
      ...additionalData,
    })
  }

  /**
   * Log de operação de push
   */
  logPush(entity, count, status, details = {}) {
    return this.log('PUSH_ENTITY', {
      entity,
      count,
      status,
      ...details,
    })
  }

  /**
   * Log de operação de pull
   */
  logPull(entity, inserted, updated, status, details = {}) {
    return this.log('PULL_ENTITY', {
      entity,
      inserted,
      updated,
      status,
      ...details,
    })
  }

  /**
   * Log de conflito detectado
   */
  logConflict(entityType, entityId, localVersion, serverVersion) {
    return this.log('CONFLICT_DETECTED', {
      entityType,
      entityId,
      localUpdatedAt: localVersion?.updated_at,
      serverUpdatedAt: serverVersion?.updatedAt,
      localStatus: localVersion?._status,
    })
  }

  /**
   * Log de operação na fila
   */
  logQueueOperation(operation, entityType, count) {
    return this.log('QUEUE_OPERATION', {
      operation,
      entityType,
      count,
    })
  }

  /**
   * Obtém contexto automático
   */
  _getContext() {
    try {
      return {
        online: typeof navigator !== 'undefined' ? navigator.onLine : true,
        url: typeof window !== 'undefined' ? window.location.pathname : '',
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        timestamp: Date.now(),
      }
    } catch {
      return { timestamp: Date.now() }
    }
  }

  /**
   * Log formatado no console
   */
  _consoleLog(entry) {
    const prefix = `[SYNC ${entry.event}]`
    const style =
      entry.event.includes('ERROR') || entry.event.includes('FAILURE')
        ? 'color: red; font-weight: bold'
        : entry.event.includes('SUCCESS')
          ? 'color: green; font-weight: bold'
          : entry.event.includes('WARNING')
            ? 'color: orange'
            : 'color: blue'

    if (typeof console !== 'undefined') {
      console.log(`%c${prefix}`, style, entry.data, entry.context)
    }
  }

  /**
   * Persistência desabilitada - logs são apenas exibidos no console
   * Motivo: localStorage tem limite de ~5MB e sync_logs causava QuotaExceededError
   * Os logs em console.log (via _consoleLog) são suficientes para debug
   */
  _persist(entry) {
    // Persistência removida para evitar QuotaExceededError
    // Logs visíveis via _consoleLog() no console do navegador
  }

  /**
   * Retorna array vazio - persistência desabilitada
   * Logs estão disponíveis apenas no console do navegador
   */
  getLogs() {
    return []
  }

  /**
   * Obtém logs filtrados por evento
   */
  getLogsByEvent(eventPattern) {
    return this.getLogs().filter((log) => log.event.includes(eventPattern))
  }

  /**
   * Obtém logs de uma sessão de sync específica
   */
  getLogsBySyncId(syncId) {
    return this.getLogs().filter((log) => log.syncId === syncId)
  }

  /**
   * Obtém logs das últimas N horas
   */
  getRecentLogs(hours = 24) {
    const cutoff = Date.now() - hours * 60 * 60 * 1000
    return this.getLogs().filter((log) => log.timestampMs >= cutoff)
  }

  /**
   * Exporta logs como JSON para download
   */
  exportLogs() {
    const logs = this.getLogs()
    const blob = new Blob([JSON.stringify(logs, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = `sync-logs-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    return logs.length
  }

  /**
   * Limpa logs legados do localStorage (se existirem)
   */
  clearLogs() {
    try {
      localStorage.removeItem(LOGS_STORAGE_KEY)
    } catch (e) {
      // Ignorar erros de limpeza
    }
  }

  /**
   * Gera relatório resumido
   */
  generateReport() {
    const logs = this.getLogs()
    const last24h = this.getRecentLogs(24)

    const report = {
      generatedAt: new Date().toISOString(),
      totalLogs: logs.length,
      last24hLogs: last24h.length,

      // Contadores por evento
      eventCounts: {},

      // Últimos erros
      recentErrors: last24h
        .filter((l) => l.event.includes('ERROR') || l.event.includes('FAILURE'))
        .slice(-10),

      // Estatísticas de sync
      syncStats: {
        total: last24h.filter((l) => l.event === 'SYNC_START').length,
        success: last24h.filter((l) => l.event === 'SYNC_SUCCESS').length,
        failure: last24h.filter((l) => l.event === 'SYNC_FAILURE').length,
      },

      // Conflitos
      conflicts: last24h.filter((l) => l.event === 'CONFLICT_DETECTED').length,
    }

    // Contar eventos
    logs.forEach((log) => {
      report.eventCounts[log.event] = (report.eventCounts[log.event] || 0) + 1
    })

    return report
  }
}

// Singleton
export const syncLogger = new SyncLogger()

export default SyncLogger
